import React, { useState } from 'react';
import { ArrowLeft, CheckCircle, Upload, Phone, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { companyInfo } from '../mockData';
import Header from './Header';
import Footer from './Footer';

const PaymentPage = () => {
  const navigate = useNavigate();
  const [paymentData, setPaymentData] = useState({
    name: '',
    phone: '',
    email: '',
    amount: '',
    transactionId: '',
    screenshot: null
  });

  const [screenshotPreview, setScreenshotPreview] = useState(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPaymentData({ ...paymentData, screenshot: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setScreenshotPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Payment submitted:', paymentData);
    toast.success('Payment details submitted successfully! We will verify and confirm shortly.');
    // Reset form
    setPaymentData({
      name: '',
      phone: '',
      email: '',
      amount: '',
      transactionId: '',
      screenshot: null
    });
    setScreenshotPreview(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="container mx-auto px-4 py-24">
        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-orange-600 hover:text-orange-700 font-medium mb-8 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Home
        </button>

        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              Complete Your <span className="text-orange-600">Payment</span>
            </h1>
            <p className="text-gray-600 text-lg">
              Scan the QR code below to make a secure payment
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* QR Code Section */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-orange-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                Scan QR Code
              </h2>

              {/* QR Code */}
              <div className="bg-gray-50 rounded-xl p-6 mb-6 flex justify-center">
                <img
                  src={companyInfo.qrCode}
                  alt="Payment QR Code"
                  className="w-full max-w-sm rounded-lg shadow-md"
                />
              </div>

              {/* Payment Instructions */}
              <div className="space-y-4">
                <div className="bg-orange-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Payment Instructions:</h3>
                  <ol className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start">
                      <span className="bg-orange-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs mr-2 flex-shrink-0 mt-0.5">1</span>
                      <span>Open your UPI app (Google Pay, PhonePe, Paytm, etc.)</span>
                    </li>
                    <li className="flex items-start">
                      <span className="bg-orange-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs mr-2 flex-shrink-0 mt-0.5">2</span>
                      <span>Scan the QR code shown above</span>
                    </li>
                    <li className="flex items-start">
                      <span className="bg-orange-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs mr-2 flex-shrink-0 mt-0.5">3</span>
                      <span>Enter the booking amount</span>
                    </li>
                    <li className="flex items-start">
                      <span className="bg-orange-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs mr-2 flex-shrink-0 mt-0.5">4</span>
                      <span>Complete the payment</span>
                    </li>
                    <li className="flex items-start">
                      <span className="bg-orange-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs mr-2 flex-shrink-0 mt-0.5">5</span>
                      <span>Take a screenshot and submit using the form</span>
                    </li>
                  </ol>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-green-900 mb-1">Secure Payment</p>
                      <p className="text-sm text-green-700">All payments are processed through secure UPI gateway</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Details Form */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-orange-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Submit Payment Details
              </h2>

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <Input
                    type="text"
                    value={paymentData.name}
                    onChange={(e) => setPaymentData({ ...paymentData, name: e.target.value })}
                    placeholder="Enter your name"
                    required
                    className="border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <Input
                    type="tel"
                    value={paymentData.phone}
                    onChange={(e) => setPaymentData({ ...paymentData, phone: e.target.value })}
                    placeholder="Enter phone number"
                    required
                    className="border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <Input
                    type="email"
                    value={paymentData.email}
                    onChange={(e) => setPaymentData({ ...paymentData, email: e.target.value })}
                    placeholder="Enter email address"
                    required
                    className="border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                  />
                </div>

                {/* Amount */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Amount Paid (₹) *
                  </label>
                  <Input
                    type="number"
                    value={paymentData.amount}
                    onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                    placeholder="Enter amount"
                    required
                    className="border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                  />
                </div>

                {/* Transaction ID */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Transaction ID / UPI Reference Number
                  </label>
                  <Input
                    type="text"
                    value={paymentData.transactionId}
                    onChange={(e) => setPaymentData({ ...paymentData, transactionId: e.target.value })}
                    placeholder="Enter transaction ID (optional)"
                    className="border-gray-300 focus:border-orange-500 focus:ring-orange-500"
                  />
                </div>

                {/* Screenshot Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload Payment Screenshot *
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-orange-500 transition-colors">
                    {screenshotPreview ? (
                      <div className="space-y-3">
                        <img
                          src={screenshotPreview}
                          alt="Screenshot preview"
                          className="max-h-48 mx-auto rounded-lg shadow-md"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setScreenshotPreview(null);
                            setPaymentData({ ...paymentData, screenshot: null });
                          }}
                          className="text-sm text-orange-600 hover:text-orange-700 font-medium"
                        >
                          Remove & Upload New
                        </button>
                      </div>
                    ) : (
                      <label className="cursor-pointer">
                        <Upload className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-600 mb-1">Click to upload screenshot</p>
                        <p className="text-xs text-gray-500">PNG, JPG up to 5MB</p>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleFileChange}
                          className="hidden"
                          required
                        />
                      </label>
                    )}
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-orange-600 hover:bg-orange-700 text-white py-6 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all"
                >
                  Submit Payment Details
                </Button>
              </form>

              {/* Contact Support */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-700 mb-2 font-medium">Need help with payment?</p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <a
                    href={`tel:${companyInfo.phone[0]}`}
                    className="flex items-center gap-2 text-sm text-orange-600 hover:text-orange-700"
                  >
                    <Phone className="w-4 h-4" />
                    {companyInfo.phone[0]}
                  </a>
                  <span className="hidden sm:inline text-gray-400">|</span>
                  <a
                    href={`mailto:${companyInfo.email}`}
                    className="flex items-center gap-2 text-sm text-orange-600 hover:text-orange-700"
                  >
                    <Mail className="w-4 h-4" />
                    {companyInfo.email}
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div className="mt-8 bg-orange-50 border border-orange-200 rounded-xl p-6">
            <h3 className="font-bold text-gray-900 mb-3">Important Notes:</h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start">
                <span className="text-orange-600 mr-2">•</span>
                <span>Please ensure you have completed the payment before submitting this form</span>
              </li>
              <li className="flex items-start">
                <span className="text-orange-600 mr-2">•</span>
                <span>Your booking will be confirmed once we verify your payment</span>
              </li>
              <li className="flex items-start">
                <span className="text-orange-600 mr-2">•</span>
                <span>You will receive a confirmation call/message within 1 hour</span>
              </li>
              <li className="flex items-start">
                <span className="text-orange-600 mr-2">•</span>
                <span>For any issues, contact us at {companyInfo.phone[0]} or {companyInfo.email}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PaymentPage;